import { Controller } from "@hotwired/stimulus"

// streak-flame
// ---------------------------------------------------------------
// HTML/CSS/JS port of the native iOS StreakChipView flame so the web
// "Active" badge matches the native nav chip exactly. The resting
// breath + halo pulse are pure CSS; this controller drives the parts
// CSS can't: three randomized ember emitters and the one-time ignition
// intro (converging sparks -> flash -> shockwave -> spring pop).
//
// Timing values mirror NavBarComponent.swift (StreakChipView):
//   embers: center / left / right variants, ignition total ~1.8s.
// Honors prefers-reduced-motion and only plays the intro once per
// browser session (sessionStorage), like the native once-per-launch.

const SESSION_KEY = "streakFlameIgnited"

// Ember emitter variants (px ≈ native points for the 16px flame).
const EMITTERS = [
  { diameter: 4.0, offset:  0.0, jitter: [-1.2, 1.2], drift: [-2.5,  2.5], rise: [14, 19], duration: [1.30, 1.70], peak: [0.95, 1.10], interval: [4.2, 5.6] },
  { diameter: 2.4, offset: -2.5, jitter: [-0.8, 0.4], drift: [-3.5, -1.0], rise: [ 9, 13], duration: [1.05, 1.40], peak: [0.80, 1.00], interval: [3.6, 5.4] },
  { diameter: 2.4, offset:  2.5, jitter: [-0.4, 0.8], drift: [ 1.0,  3.5], rise: [ 9, 13], duration: [1.05, 1.40], peak: [0.80, 1.00], interval: [3.6, 5.4] }
]

// Converging-spark origins for the ignition (scaled from native 30px chip).
const SPARK_ORIGINS = [[-15, -11], [14, -13], [-17, 8], [17, 10], [0, -15], [0, 13]]

export default class extends Controller {
  static targets = ["flame", "front", "back", "halo", "embers", "label"]

  connect() {
    this._timers = []
    this._anims = []

    if (this._reducedMotion()) {
      this._revealReduced()
      return
    }

    if (sessionStorage.getItem(SESSION_KEY) === "1") {
      this._startEmbers()
    } else {
      this._playIgnition()
    }
  }

  disconnect() {
    this._timers.forEach((t) => clearTimeout(t))
    this._anims.forEach((a) => { try { a.cancel() } catch (_) {} })
    this._timers = []
    this._anims = []
  }

  // ── helpers ──────────────────────────────────────────────
  _reducedMotion() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  _rand(range) { return range[0] + Math.random() * (range[1] - range[0]) }

  _later(fn, ms) {
    const t = setTimeout(fn, ms)
    this._timers.push(t)
    return t
  }

  _animate(el, frames, opts) {
    const a = el.animate(frames, opts)
    this._anims.push(a)
    return a
  }

  // ── reduced motion: just show the resting flame, no loops ─
  _revealReduced() {
    if (this.hasLabelTarget) this.labelTarget.style.opacity = "1"
  }

  // ── continuous embers ────────────────────────────────────
  _startEmbers() {
    EMITTERS.forEach((cfg, i) => {
      // Stagger first release: 0.6 + 0.8*i + rand(0..0.4) s.
      const first = (0.6 + 0.8 * i + Math.random() * 0.4) * 1000
      this._later(() => this._emit(cfg), first)
    })
  }

  _scheduleNext(cfg) {
    this._later(() => this._emit(cfg), this._rand(cfg.interval) * 1000)
  }

  _emit(cfg) {
    if (!this.hasEmbersTarget) return

    const baseX = cfg.offset + this._rand(cfg.jitter)
    const drift = this._rand(cfg.drift)
    const rise = this._rand(cfg.rise)
    const dur = this._rand(cfg.duration) * 1000
    const peak = this._rand(cfg.peak)
    const xMid = baseX + drift * 0.45
    const xEnd = baseX + drift

    const ember = document.createElement("span")
    ember.className = "streak-flame-ember"
    ember.style.width = `${cfg.diameter}px`
    ember.style.height = `${cfg.diameter}px`
    ember.style.marginLeft = `${-cfg.diameter / 2}px`
    this.embersTarget.appendChild(ember)

    const a = this._animate(
      ember,
      [
        { transform: `translate(${baseX}px, 0px) scale(0.55)`, opacity: 0, offset: 0 },
        { transform: `translate(${baseX}px, ${-rise * 0.18}px) scale(0.55)`, opacity: 1, offset: 0.20 },
        { transform: `translate(${xMid}px, ${-rise * 0.40}px) scale(${peak})`, opacity: 1, offset: 0.35 },
        { transform: `translate(${xMid}px, ${-rise * 0.62}px) scale(${peak})`, opacity: 0.85, offset: 0.55 },
        { transform: `translate(${xEnd}px, ${-rise * 0.80}px) scale(${peak * 0.5})`, opacity: 0.5, offset: 0.75 },
        { transform: `translate(${xEnd}px, ${-rise}px) scale(0.25)`, opacity: 0, offset: 1 }
      ],
      { duration: dur, easing: "cubic-bezier(0.2, 0.6, 0.2, 1)", fill: "forwards" }
    )

    a.onfinish = () => {
      ember.remove()
      this._scheduleNext(cfg)
    }
  }

  // ── one-time ignition intro (~1.8s) ──────────────────────
  _playIgnition() {
    sessionStorage.setItem(SESSION_KEY, "1")

    const flame = this.flameTarget
    flame.classList.add("is-igniting")
    if (this.hasLabelTarget) this.labelTarget.style.opacity = "0"

    // 0.30s: six sparks converge on the flame center.
    this._later(() => this._spawnSparks(), 300)

    // ~1.10s: ignition flash + shockwave.
    this._later(() => { this._flash(); this._shockwave() }, 1100)

    // ~1.28s: reveal the flame (spring) + raise the label.
    this._later(() => this._revealFlame(), 1280)

    // ~1.80s: settle into the continuous ember loops.
    this._later(() => {
      flame.classList.remove("is-igniting")
      this._startEmbers()
    }, 1800)
  }

  _spawnSparks() {
    SPARK_ORIGINS.forEach(([ox, oy], i) => {
      const spark = document.createElement("span")
      spark.className = "streak-flame-spark"
      this.flameTarget.appendChild(spark)

      const a = this._animate(
        spark,
        [
          { transform: `translate(${ox}px, ${oy}px) scale(0.2)`, opacity: 0, offset: 0 },
          { transform: `translate(${ox * 0.55}px, ${oy * 0.55}px) scale(0.85)`, opacity: 1, offset: 0.18 },
          { opacity: 1, offset: 0.82 },
          { transform: "translate(0px, 0px) scale(0.25)", opacity: 0, offset: 1 }
        ],
        { duration: 780, delay: i * 50, easing: "cubic-bezier(0.25, 0.8, 0.4, 1)", fill: "forwards" }
      )
      a.onfinish = () => spark.remove()
    })
  }

  _flash() {
    const flash = document.createElement("span")
    flash.className = "streak-flame-flash"
    this.flameTarget.appendChild(flash)

    const a = this._animate(
      flash,
      [
        { transform: "scale(0)", opacity: 0, offset: 0 },
        { transform: "scale(2.4)", opacity: 1, offset: 0.4 },
        { transform: "scale(3.2)", opacity: 0, offset: 1 }
      ],
      { duration: 300, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)", fill: "forwards" }
    )
    a.onfinish = () => flash.remove()
  }

  _shockwave() {
    const ring = document.createElement("span")
    ring.className = "streak-flame-shock"
    this.flameTarget.appendChild(ring)

    const a = this._animate(
      ring,
      [
        { transform: "scale(0.4)", opacity: 0, offset: 0 },
        { transform: "scale(2.5)", opacity: 0.9, offset: 0.2 },
        { transform: "scale(7.5)", opacity: 0, offset: 1 }
      ],
      { duration: 700, easing: "cubic-bezier(0.25, 0.6, 0.2, 1)", fill: "forwards" }
    )
    a.onfinish = () => ring.remove()
  }

  _revealFlame() {
    this.flameTarget.classList.remove("is-igniting")

    // Front flame: springy pop from nothing (mirrors the native spring).
    if (this.hasFrontTarget) {
      this._animate(
        this.frontTarget,
        [
          { transform: "scale(0)", opacity: 0, offset: 0 },
          { transform: "scale(1.18)", opacity: 1, offset: 0.55 },
          { transform: "scale(0.94)", offset: 0.78 },
          { transform: "scale(1.03)", offset: 0.9 },
          { transform: "scale(1)", offset: 1 }
        ],
        { duration: 500, easing: "ease-out", fill: "backwards" }
      )
    }

    // Label rise: translateY +4 -> 0, scale 0.85 -> 1, fade in.
    if (this.hasLabelTarget) {
      this.labelTarget.style.opacity = "1"
      this._animate(
        this.labelTarget,
        [
          { transform: "translateY(4px) scale(0.85)", opacity: 0, offset: 0 },
          { transform: "translateY(0) scale(1)", opacity: 1, offset: 1 }
        ],
        { duration: 450, delay: 40, easing: "cubic-bezier(0.2, 0.9, 0.3, 1.2)", fill: "backwards" }
      )
    }
  }
}
